import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, DollarSign, Users, Zap, Award,
  ArrowUpRight, ChevronRight, Star, Crown, Flame,
  BarChart3, Target, Eye,
} from 'lucide-react';

// ─── Whale Watch — Crypto whale tracking but for top servers ────────
// "Who are the whales? What are they doing? Copy-trade the best."

interface Whale {
  name: string;
  avatar: string;
  restaurant: string;
  city: string;
  tier: 'diamond' | 'gold';
  monthlyEarnings: number;
  upsellRate: number;
  signature: string;
  topMove: string;
  streak: number;
  followersCount: number;
  isFollowed: boolean;
  recentMoves: { action: string; result: string; time: string }[];
}

const WHALES: Whale[] = [
  {
    name: 'Emma M.', avatar: 'EM', restaurant: 'Le Bernardin', city: 'NYC',
    tier: 'diamond', monthlyEarnings: 4230, upsellRate: 93, signature: 'The Storyteller',
    topMove: 'Opens with a personal tasting note — "I had the halibut last night and couldn\'t stop thinking about it"',
    streak: 14, followersCount: 234, isFollowed: true,
    recentMoves: [
      { action: 'Landed 8-top tasting menu upsell', result: '+$340 table', time: '2h ago' },
      { action: 'Wine pairing suggestion accepted', result: '+$120 per couple', time: '3h ago' },
      { action: 'Perfect greeting streak: 12', result: 'New PB', time: '5h ago' },
    ],
  },
  {
    name: 'Sarah C.', avatar: 'SC', restaurant: 'SAJJ Mediterranean', city: 'SF',
    tier: 'diamond', monthlyEarnings: 3890, upsellRate: 87, signature: 'The Closer',
    topMove: 'Never asks "would you like" — instead says "you\'re going to love" and starts describing',
    streak: 9, followersCount: 187, isFollowed: false,
    recentMoves: [
      { action: '$WAGYU: 5 closes in lunch rush', result: '83% hit rate', time: '1h ago' },
      { action: 'New technique: menu walk-through', result: '+22% conversion', time: '4h ago' },
      { action: 'Beat her own daily record', result: '$312 shift', time: 'Yesterday' },
    ],
  },
  {
    name: 'Jake B.', avatar: 'JB', restaurant: 'Alinea', city: 'CHI',
    tier: 'diamond', monthlyEarnings: 3650, upsellRate: 90, signature: 'The Scientist',
    topMove: 'A/B tests his approaches and tracks which phrases convert best for each menu item',
    streak: 11, followersCount: 156, isFollowed: false,
    recentMoves: [
      { action: 'Discovered: "chef\'s recommendation" > "special"', result: '+15% conversion', time: '3h ago' },
      { action: 'Dessert upsell new strategy', result: '4/4 closes', time: '6h ago' },
      { action: 'Published new play: "The Triple Down"', result: '23 saves', time: 'Yesterday' },
    ],
  },
];

interface PlaybookEntry {
  name: string;
  author: string;
  description: string;
  successRate: number;
  saves: number;
  category: 'opening' | 'upsell' | 'drink' | 'recovery';
}

const TRENDING_PLAYS: PlaybookEntry[] = [
  { name: 'The Taste Test', author: 'Emma M.', description: '"I tried the [item] myself last night — let me tell you what I loved about it..."', successRate: 78, saves: 89, category: 'upsell' },
  { name: 'The Comparison Close', author: 'Sarah C.', description: '"Most people go with the [regular], but the ones who try [premium] always come back for it"', successRate: 72, saves: 67, category: 'upsell' },
  { name: 'The Warm Return', author: 'Jake B.', description: '"Hey! Great to see you back. Remember what you loved last time? Wait till you try..."', successRate: 85, saves: 134, category: 'opening' },
  { name: 'The Soft Recovery', author: 'Maria G.', description: '"Totally hear you — the [regular] is excellent too. But can I at least bring a sample?"', successRate: 41, saves: 56, category: 'recovery' },
  { name: 'The Pairing Play', author: 'Emma M.', description: '"That [entree] pairs insanely well with our [drink]. Want me to bring one?"', successRate: 68, saves: 78, category: 'drink' },
];

const categoryColors = {
  opening: { text: 'text-blue-400', bg: 'bg-blue-400/10' },
  upsell: { text: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  drink: { text: 'text-purple-400', bg: 'bg-purple-400/10' },
  recovery: { text: 'text-amber-400', bg: 'bg-amber-400/10' },
};

// ─── Component ──────────────────────────────────────────────────────

const WhaleWatch: React.FC = () => {
  const navigate = useNavigate();
  const [followState, setFollowState] = useState<Record<string, boolean>>({
    'Emma M.': true,
  });
  const [expandedWhale, setExpandedWhale] = useState<string | null>(null);

  const toggleFollow = (name: string) => {
    setFollowState(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-b border-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3 mb-1">
            <Eye className="w-7 h-7 text-indigo-400" />
            <h1 className="text-2xl font-bold">Whale Watch</h1>
          </div>
          <p className="text-gray-500 text-sm">Track the top earners. Learn their moves. Copy their playbook.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Whale cards */}
        <div className="space-y-4">
          <h2 className="text-xs text-gray-600 uppercase tracking-wider flex items-center gap-2">
            <Crown className="w-3.5 h-3.5 text-amber-400" /> Top Earners This Month
          </h2>

          {WHALES.map((whale, idx) => {
            const isExpanded = expandedWhale === whale.name;
            const isFollowed = followState[whale.name];
            return (
              <div key={whale.name} className="bg-gray-950 rounded-xl border border-gray-900 overflow-hidden">
                {/* Main row */}
                <button
                  onClick={() => setExpandedWhale(isExpanded ? null : whale.name)}
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-900/50 transition-colors"
                >
                  {/* Rank */}
                  <div className="text-lg font-black text-gray-700 w-6">#{idx + 1}</div>

                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white">
                      {whale.avatar}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-cyan-400 border-2 border-black flex items-center justify-center">
                      <Crown className="w-2.5 h-2.5 text-black" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white">{whale.name}</span>
                      <span className="text-[10px] text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded-full font-bold">
                        {whale.signature}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{whale.restaurant} · {whale.city}</div>
                  </div>

                  {/* Stats */}
                  <div className="hidden sm:flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm font-bold text-emerald-400">${whale.monthlyEarnings.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-600">this month</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-white">{whale.upsellRate}%</div>
                      <div className="text-[10px] text-gray-600">upsell rate</div>
                    </div>
                    <div className="flex items-center gap-1 text-orange-400">
                      <Flame className="w-3.5 h-3.5" />
                      <span className="text-sm font-bold">{whale.streak}</span>
                    </div>
                  </div>

                  <ChevronRight className={`w-4 h-4 text-gray-700 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-900 p-5 bg-gray-900/30 space-y-4">
                    {/* Top move */}
                    <div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">Signature Move</div>
                      <p className="text-sm text-gray-300 italic">"{whale.topMove}"</p>
                    </div>

                    {/* Recent activity */}
                    <div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Recent Activity</div>
                      <div className="space-y-2">
                        {whale.recentMoves.map((move, i) => (
                          <div key={i} className="flex items-center gap-3 text-xs">
                            <Zap className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                            <span className="text-gray-400 flex-1">{move.action}</span>
                            <span className="text-emerald-400 font-medium flex-shrink-0">{move.result}</span>
                            <span className="text-gray-700 flex-shrink-0">{move.time}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Follow button */}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-xs text-gray-600">{whale.followersCount + (isFollowed ? 1 : 0)} followers</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFollow(whale.name); }}
                        className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                          isFollowed
                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                            : 'bg-indigo-600 text-white hover:bg-indigo-500'
                        }`}
                      >
                        {isFollowed ? 'Following' : 'Follow'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Trending Playbook */}
        <div>
          <h2 className="text-xs text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Trending Plays This Week
          </h2>
          <div className="space-y-2">
            {TRENDING_PLAYS.map((play, i) => {
              const cc = categoryColors[play.category];
              return (
                <div key={i} className="bg-gray-950 rounded-xl border border-gray-900 p-4 hover:border-gray-800 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cc.bg} ${cc.text}`}>
                        {play.category}
                      </span>
                      <span className="text-sm font-bold text-white">{play.name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <Star className="w-3 h-3" />{play.saves}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 italic mb-2">{play.description}</p>
                  <div className="flex items-center gap-4 text-[10px] text-gray-600">
                    <span>by <span className="text-gray-400">{play.author}</span></span>
                    <span><span className="text-emerald-400 font-bold">{play.successRate}%</span> success rate</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Copy-trade CTA */}
        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6 text-center">
          <Users className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-white mb-2">Copy the best. Become the best.</h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto mb-1">
            Follow top earners to see their techniques in your feed. Save plays to your personal loadout.
            The best servers aren't keeping secrets — they're sharing alpha.
          </p>
          <p className="text-[10px] text-indigo-400 mt-3 font-medium">
            Servers who follow at least 3 whales improve their upsell rate by 18% on average.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhaleWatch;
